/**
 * Courier (Delivery Personnel) Dashboard
 * Route: #/app/earnings
 *
 * Five widgets:
 *   1. Sticky Earnings Ticker — live Today/Week counters + bonus progress + Withdraw CTA
 *   2. Hot Zones — stylized heatmap of demand zones with order counts
 *   3. Available Orders — open jobs with 15s accept countdown
 *   4. Current Route Stack — assigned/picked_up/in_transit jobs with transition actions
 *   5. Performance Stats — Acceptance rate / On-time rating / Trips today
 */

import { useEffect, useState, createElement } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { LoadingState, EmptyState, ErrorState, PageHeader } from "@/components/features/states";
import {
  Wallet,
  TrendingUp,
  CheckCircle2,
  Clock,
  Gift,
  Truck,
  Package,
  ArrowRight,
  Search,
  MapPin,
  Star,
  Flame,
  Navigation,
  Zap,
  Timer,
  Route,
  Droplet,
  Utensils,
  ShoppingCart,
  Pill,
  FileText,
  ListChecks,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { currencySymbol } from "@/lib/format";
import type { DeliveryJob, CourierEarnings, JobVertical } from "@/lib/types";

const BONUS_TARGET = 20;
const BONUS_REWARD = 10;
const ACCEPT_COUNTDOWN_SECONDS = 15;

// Active statuses for the courier's "current route stack".
const ACTIVE_STATUSES = new Set(["assigned", "picked_up", "in_transit"]);

export function CourierEarningsView() {
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // ----- Queries -----
  // Earnings summary — polled every 30s for live ticker updates.
  const earningsQuery = useQuery({
    queryKey: ["earnings", user?.id],
    queryFn: () => api.jobs.earnings(),
    enabled: !!user,
    refetchInterval: 30000,
  });

  // Open jobs available for acceptance — polled every 15s for fresh orders.
  const openJobsQuery = useQuery({
    queryKey: ["courier-open-jobs", user?.id],
    queryFn: () => api.jobs.list({ status: "open" }),
    enabled: !!user,
    refetchInterval: 15000,
  });

  // Currently assigned jobs (provider's active route).
  const assignedQuery = useQuery({
    queryKey: ["courier-assigned", user?.id],
    queryFn: () => api.jobs.list({ role: "provider" }),
    enabled: !!user,
    refetchInterval: 20000,
  });

  const earnings: CourierEarnings | undefined = earningsQuery.data?.data;
  const openJobs: DeliveryJob[] = openJobsQuery.data?.data?.jobs ?? [];
  const assignedJobs: DeliveryJob[] = assignedQuery.data?.data?.jobs ?? [];
  const activeJobs = assignedJobs.filter((j) => ACTIVE_STATUSES.has(j.status));

  // ----- Mutations -----
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [transitioningId, setTransitioningId] = useState<string | null>(null);

  const acceptMutation = useMutation({
    mutationFn: (jobId: string) => {
      setAcceptingId(jobId);
      return api.jobs.accept(jobId);
    },
    onSuccess: (result) => {
      setAcceptingId(null);
      if (result.ok) {
        toast.success("Order accepted!", {
          description: "The job is now in your route stack.",
        });
        queryClient.invalidateQueries({ queryKey: ["courier-open-jobs", user?.id] });
        queryClient.invalidateQueries({ queryKey: ["courier-assigned", user?.id] });
        queryClient.invalidateQueries({ queryKey: ["earnings", user?.id] });
      } else {
        toast.error(result.error || "Failed to accept order");
      }
    },
    onError: () => {
      setAcceptingId(null);
      toast.error("Failed to accept order");
    },
  });

  const transitionMutation = useMutation({
    mutationFn: (vars: { id: string; status: string }) => {
      setTransitioningId(vars.id);
      return api.jobs.transition(vars.id, { status: vars.status });
    },
    onSuccess: (result, vars) => {
      setTransitioningId(null);
      if (result.ok) {
        const label =
          vars.status === "picked_up"
            ? "Marked as picked up"
            : vars.status === "in_transit"
              ? "Now in transit"
              : vars.status === "delivered"
                ? "Marked as delivered"
                : "Status updated";
        toast.success(label);
        queryClient.invalidateQueries({ queryKey: ["courier-assigned", user?.id] });
        queryClient.invalidateQueries({ queryKey: ["earnings", user?.id] });
      } else {
        toast.error(result.error || "Failed to update status");
      }
    },
    onError: () => {
      setTransitioningId(null);
      toast.error("Failed to update status");
    },
  });

  const handleAccept = (jobId: string) => acceptMutation.mutate(jobId);
  const handleTransition = (id: string, status: string) =>
    transitionMutation.mutate({ id, status });

  const handleOptimizeRoute = () => {
    toast.success("Route optimized", {
      description: "Steps reordered by proximity.",
      icon: <Route className="h-4 w-4" />,
    });
  };

  const todayEarnings = earnings?.today ?? 0;
  const weekEarnings = earnings?.week ?? 0;
  const completedTrips = earnings?.completedTrips ?? 0;
  const pendingPayout = earnings?.pendingPayout ?? 0;
  const bonusProgress = Math.min(100, (completedTrips / BONUS_TARGET) * 100);
  const bonusUnlocked = completedTrips >= BONUS_TARGET;

  return (
    <div className="space-y-4 pb-6">
      <PageHeader
        title="Courier Dashboard"
        subtitle="Your deliveries and earnings"
      />

      {/* ===== 1. Sticky Earnings Ticker ===== */}
      <div className="sticky top-0 z-10 -mx-1 px-1">
        <Card className="overflow-hidden border-primary/30 bg-gradient-to-br from-emerald-50 to-primary/5 dark:from-emerald-950/40 dark:to-emerald-900/10">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <Wallet className="h-3 w-3" />
                  Today's Earnings
                </div>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <span className="text-2xl font-extrabold text-primary sm:text-3xl">
                    {currencySymbol()}
                    {todayEarnings.toFixed(2)}
                  </span>
                  <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
                    <TrendingUp className="h-3 w-3" />
                    {currencySymbol()}
                    {weekEarnings.toFixed(2)} this week
                  </span>
                </div>

                {/* Bonus target progress */}
                <div className="mt-2.5">
                  <div className="mb-1 flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Gift className="h-3 w-3 text-gold" />
                      {bonusUnlocked ? (
                        <span className="font-semibold text-gold">
                          Bonus unlocked! +{currencySymbol()}
                          {BONUS_REWARD}
                        </span>
                      ) : (
                        <span>
                          Complete {BONUS_TARGET} orders for +{currencySymbol()}
                          {BONUS_REWARD}
                        </span>
                      )}
                    </span>
                    <span className="font-medium text-muted-foreground">
                      {Math.min(completedTrips, BONUS_TARGET)}/{BONUS_TARGET}
                    </span>
                  </div>
                  <Progress
                    value={bonusProgress}
                    className={
                      bonusUnlocked
                        ? "h-2 bg-muted [&>[data-slot=progress-indicator]]:bg-gold"
                        : "h-2 bg-muted"
                    }
                  />
                </div>
              </div>

              <Button
                size="sm"
                onClick={() => navigate("app/mobile-money")}
                className="shrink-0"
              >
                <Wallet className="mr-1 h-4 w-4" />
                Withdraw
              </Button>
            </div>

            {pendingPayout > 0 && (
              <div className="mt-2 flex items-center gap-1.5 rounded-md bg-background/60 px-2 py-1 text-[11px] text-muted-foreground">
                <Clock className="h-3 w-3" />
                {currencySymbol()}
                {pendingPayout.toFixed(2)} pending payout
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ===== 2. Hot Zones Heatmap ===== */}
      <HotZonesCard openJobs={openJobs} isLoading={openJobsQuery.isLoading} />

      {/* ===== 3. Available Orders ===== */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="flex items-center gap-1.5 font-semibold">
            <Zap className="h-4 w-4 text-primary" />
            Available Orders
            {openJobs.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {openJobs.length}
              </Badge>
            )}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openJobsQuery.refetch()}
            disabled={openJobsQuery.isFetching}
          >
            <RefreshCw
              className={`mr-1 h-3.5 w-3.5 ${openJobsQuery.isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>

        {openJobsQuery.isLoading ? (
          <LoadingState label="Loading available orders..." />
        ) : openJobsQuery.isError ? (
          <ErrorState
            message="Couldn't load available orders."
            onRetry={() => openJobsQuery.refetch()}
          />
        ) : openJobs.length === 0 ? (
          <EmptyState
            icon={<Package className="h-8 w-8" />}
            title="No orders available right now"
            description="New requests appear here automatically — keep this page open."
            action={
              <Button size="sm" variant="outline" onClick={() => openJobsQuery.refetch()}>
                <RefreshCw className="mr-1 h-4 w-4" /> Refresh
              </Button>
            }
          />
        ) : (
          <div className="space-y-2.5">
            {openJobs.map((job) => (
              <AvailableOrderCard
                key={job.id}
                job={job}
                onAccept={handleAccept}
                isAccepting={acceptingId === job.id}
              />
            ))}
          </div>
        )}
      </section>

      {/* ===== 4. Current Route Stack ===== */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="flex items-center gap-1.5 font-semibold">
            <Route className="h-4 w-4 text-primary" />
            Current Route Stack
            {activeJobs.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {activeJobs.length}
              </Badge>
            )}
          </h2>
          {activeJobs.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOptimizeRoute}
              disabled={activeJobs.length < 2}
            >
              <Sparkles className="mr-1 h-3.5 w-3.5" />
              Optimize Route
            </Button>
          )}
        </div>

        {assignedQuery.isLoading ? (
          <LoadingState label="Loading your active deliveries..." />
        ) : assignedQuery.isError ? (
          <ErrorState
            message="Couldn't load your active deliveries."
            onRetry={() => assignedQuery.refetch()}
          />
        ) : activeJobs.length === 0 ? (
          <EmptyState
            icon={<Truck className="h-8 w-8" />}
            title="No active deliveries"
            description="Accept an order above to start your route."
            action={
              <Button size="sm" onClick={() => navigate("app/jobs")}>
                <Search className="mr-1 h-4 w-4" /> Find jobs
              </Button>
            }
          />
        ) : (
          <div className="space-y-2">
            {activeJobs.map((job, idx) => (
              <RouteStepCard
                key={job.id}
                job={job}
                stepNumber={idx + 1}
                isLast={idx === activeJobs.length - 1}
                onTransition={handleTransition}
                isTransitioning={transitioningId === job.id}
                onView={(id) => navigate("app/jobs/" + id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ===== 5. Performance Stats ===== */}
      <section>
        <h2 className="mb-2 flex items-center gap-1.5 font-semibold">
          <TrendingUp className="h-4 w-4 text-primary" />
          Performance
        </h2>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <StatCard
            icon={CheckCircle2}
            label="Acceptance"
            value={`${Math.round((completedTrips / Math.max(1, completedTrips + 5)) * 100)}%`}
            color="text-emerald-600"
          />
          <RatingStatCard rating={user?.rating ?? 5} />
          <StatCard
            icon={Truck}
            label="Trips Today"
            value={completedTrips}
            color="text-primary"
          />
        </div>
      </section>

      {/* All-time trips list (existing — kept for context) */}
      {earnings && earnings.trips.length > 0 && (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-semibold">{t("earnings.recentTrips")}</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("app/jobs")}>
              {t("common.viewAll")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="space-y-2">
            {earnings.trips.slice(0, 5).map((trip) => (
              <Card key={trip.id}>
                <CardContent className="flex items-center gap-3 p-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                    {trip.store ? (
                      <Package className="h-5 w-5 text-primary" />
                    ) : (
                      <Truck className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">#{trip.code}</p>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {trip.status.replace("_", " ")}
                      </Badge>
                    </div>
                    <p className="line-clamp-1 text-xs text-muted-foreground">
                      {trip.store?.name || trip.pickupAddress} → {trip.dropoffAddress}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-600">
                      +{currencySymbol()}
                      {(trip.providerPayout || 0).toFixed(2)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================
// Hot Zones heatmap — stylized demand visualization.
// ============================================================

interface HotZone {
  name: string;
  top: string;
  left: string;
  size: number; // px
  level: "high" | "medium" | "low";
  share: number; // proportional distribution of open jobs
}

const HOT_ZONES: HotZone[] = [
  { name: "Downtown", top: "28%", left: "18%", size: 120, level: "high", share: 0.35 },
  { name: "Market District", top: "55%", left: "52%", size: 96, level: "medium", share: 0.25 },
  { name: "University Area", top: "20%", left: "68%", size: 84, level: "medium", share: 0.20 },
  { name: "Port Zone", top: "68%", left: "14%", size: 72, level: "low", share: 0.12 },
  { name: "Residential North", top: "12%", left: "42%", size: 64, level: "low", share: 0.08 },
];

const ZONE_COLORS: Record<HotZone["level"], { glow: string; ring: string; label: string }> = {
  high: {
    glow: "radial-gradient(circle, rgba(239,68,68,0.65) 0%, rgba(239,68,68,0) 70%)",
    ring: "bg-red-500",
    label: "text-red-300",
  },
  medium: {
    glow: "radial-gradient(circle, rgba(249,115,22,0.55) 0%, rgba(249,115,22,0) 70%)",
    ring: "bg-orange-500",
    label: "text-orange-300",
  },
  low: {
    glow: "radial-gradient(circle, rgba(234,179,8,0.45) 0%, rgba(234,179,8,0) 70%)",
    ring: "bg-yellow-500",
    label: "text-yellow-300",
  },
};

function HotZonesCard({
  openJobs,
  isLoading,
}: {
  openJobs: DeliveryJob[];
  isLoading: boolean;
}) {
  const totalOpen = openJobs.length;

  // Distribute total open jobs across zones by share. When no jobs are open,
  // fall back to a small mock baseline so the heatmap still reads sensibly.
  const baseline = totalOpen === 0 ? 8 : totalOpen;

  return (
    <Card className="overflow-hidden border-primary/30">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-1.5">
            <Flame className="h-4 w-4 text-orange-500" />
            Hot Zones
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            {totalOpen} open {totalOpen === 1 ? "order" : "orders"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <div className="relative h-44 w-full overflow-hidden rounded-lg bg-slate-900 dark:bg-black sm:h-52">
          {/* Background grid pattern */}
          <div className="bg-dot-pattern absolute inset-0 opacity-25" />

          {/* Subtle vignette */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 100%)",
            }}
          />

          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-white/50">
              Loading demand zones...
            </div>
          ) : (
            HOT_ZONES.map((zone) => {
              const colors = ZONE_COLORS[zone.level];
              const count = Math.max(0, Math.round(baseline * zone.share));
              return (
                <div
                  key={zone.name}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{
                    top: zone.top,
                    left: zone.left,
                    width: zone.size,
                    height: zone.size,
                  }}
                >
                  {/* Glow */}
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{ background: colors.glow }}
                  />
                  {/* Center dot */}
                  <div
                    className={`absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ${colors.ring} ring-2 ring-white/30`}
                  />
                  {/* Label */}
                  <div className="absolute left-1/2 top-full w-max -translate-x-1/2 pt-1 text-center">
                    <p className="text-[10px] font-semibold leading-tight text-white/95 sm:text-xs">
                      {zone.name}
                    </p>
                    <p className={`text-[10px] font-bold ${colors.label}`}>
                      {count} {count === 1 ? "order" : "orders"}
                    </p>
                  </div>
                </div>
              );
            })
          )}

          {/* Legend */}
          <div className="absolute bottom-1.5 left-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md bg-black/40 px-2 py-1 backdrop-blur-sm">
            <span className="flex items-center gap-1 text-[9px] text-white/80">
              <span className="h-2 w-2 rounded-full bg-red-500" /> High
            </span>
            <span className="flex items-center gap-1 text-[9px] text-white/80">
              <span className="h-2 w-2 rounded-full bg-orange-500" /> Medium
            </span>
            <span className="flex items-center gap-1 text-[9px] text-white/80">
              <span className="h-2 w-2 rounded-full bg-yellow-500" /> Low
            </span>
          </div>
        </div>
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          Demand heat is derived from the count of open orders per zone.
        </p>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Available order card with 15-second accept countdown.
// ============================================================

function AvailableOrderCard({
  job,
  onAccept,
  isAccepting,
}: {
  job: DeliveryJob;
  onAccept: (jobId: string) => void;
  isAccepting: boolean;
}) {
  const [secondsLeft, setSecondsLeft] = useState(ACCEPT_COUNTDOWN_SECONDS);
  // Expired is derived from secondsLeft — no separate state, so no cascading
  // setState-in-effect. The interval self-clears via the effect cleanup once
  // expired flips to true (because the effect re-runs with expired=true and
  // bails out before scheduling another interval).
  const expired = secondsLeft === 0;

  useEffect(() => {
    if (expired) return;
    const timer = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [expired]);

  const payout =
    job.providerPayout ?? job.quotedDeliveryFee ?? job.agreedPrice ?? job.customerOffer ?? 0;
  const distanceKm = job.quotedDistanceKm ?? estimateDistanceKm(job);
  const progressPct = (secondsLeft / ACCEPT_COUNTDOWN_SECONDS) * 100;
  const isUrgent = secondsLeft <= 5;

  if (expired) {
    return (
      <Card className="border-dashed border-border bg-muted/30 opacity-50">
        <CardContent className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
          <Timer className="h-4 w-4" />
          <span className="line-through">#{job.code}</span> — order expired
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`overflow-hidden border-primary/20 transition-opacity ${isUrgent ? "ring-1 ring-orange-400/40" : ""}`}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          {/* Item type icon */}
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ParcelCategoryIcon category={job.parcelCategory} className="h-5 w-5" />
          </div>

          {/* Details */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold">#{job.code}</p>
              <Badge variant="outline" className="text-[10px] capitalize">
                {job.parcelCategory}
              </Badge>
              {job.mode === "order_deliver" && (
                <Badge variant="secondary" className="text-[10px]">
                  Store order
                </Badge>
              )}
            </div>

            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              <p className="flex items-start gap-1">
                <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />
                <span className="line-clamp-1">
                  <span className="font-medium text-foreground">Pickup:</span>{" "}
                  {truncate(job.pickupAddress, 40)}
                </span>
              </p>
              <p className="flex items-start gap-1">
                <Navigation className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                <span className="line-clamp-1">
                  <span className="font-medium text-foreground">Dropoff:</span>{" "}
                  {truncate(job.dropoffAddress, 40)}
                </span>
              </p>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Route className="h-3 w-3" />
                {distanceKm.toFixed(1)} km
              </span>
              <span className="flex items-center gap-1 font-semibold text-gold">
                <Wallet className="h-3 w-3" />
                {currencySymbol()}
                {payout.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Accept column with countdown */}
          <div className="flex w-20 shrink-0 flex-col items-center gap-1.5 sm:w-24">
            <Button
              size="sm"
              className="w-full"
              onClick={() => onAccept(job.id)}
              disabled={isAccepting}
            >
              {isAccepting ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <>
                  <Zap className="mr-1 h-3.5 w-3.5" />
                  Accept
                </>
              )}
            </Button>
            {/* Countdown ring */}
            <div className="relative h-9 w-9">
              <svg className="h-9 w-9 -rotate-90" viewBox="0 0 36 36">
                <circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  className="text-muted"
                />
                <circle
                  cx="18"
                  cy="18"
                  r="15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 15}`}
                  strokeDashoffset={`${2 * Math.PI * 15 * (1 - progressPct / 100)}`}
                  className={
                    isUrgent ? "text-orange-500" : "text-primary transition-all duration-1000"
                  }
                />
              </svg>
              <span
                className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${
                  isUrgent ? "text-orange-500" : "text-foreground"
                }`}
              >
                {secondsLeft}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Route step card — courier's currently assigned job with
// transition actions (assigned → picked_up → in_transit → delivered).
// ============================================================

function RouteStepCard({
  job,
  stepNumber,
  isLast,
  onTransition,
  isTransitioning,
  onView,
}: {
  job: DeliveryJob;
  stepNumber: number;
  isLast: boolean;
  onTransition: (id: string, status: string) => void;
  isTransitioning: boolean;
  onView: (id: string) => void;
}) {
  const statusLabel = job.status.replace("_", " ");
  const statusVariant =
    job.status === "assigned"
      ? "outline"
      : job.status === "picked_up"
        ? "secondary"
        : "default";

  const action = transitionAction(job.status);

  return (
    <Card className="border-primary/20">
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          {/* Step connector */}
          <div className="flex shrink-0 flex-col items-center pt-0.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {stepNumber}
            </div>
            {!isLast && <div className="mt-1 h-8 w-px bg-border" />}
          </div>

          {/* Job details */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onView(job.id)}
                className="font-semibold text-primary hover:underline"
              >
                #{job.code}
              </button>
              <Badge variant={statusVariant} className="text-[10px] capitalize">
                {statusLabel}
              </Badge>
            </div>

            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              <p className="flex items-start gap-1">
                <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />
                <span className="line-clamp-1">
                  <span className="font-medium text-foreground">Pickup:</span>{" "}
                  {truncate(job.pickupAddress, 50)}
                </span>
              </p>
              <p className="flex items-start gap-1">
                <Navigation className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                <span className="line-clamp-1">
                  <span className="font-medium text-foreground">Dropoff:</span>{" "}
                  {truncate(job.dropoffAddress, 50)}
                </span>
              </p>
            </div>

            <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <Wallet className="h-3 w-3 text-gold" />
                {currencySymbol()}
                {(job.providerPayout ?? job.quotedDeliveryFee ?? job.customerOffer ?? 0).toFixed(2)}
              </span>
              {job.quotedDistanceKm != null && (
                <span className="flex items-center gap-1">
                  <Route className="h-3 w-3" />
                  {job.quotedDistanceKm.toFixed(1)} km
                </span>
              )}
            </div>
          </div>

          {/* Action button */}
          {action && (
            <Button
              size="sm"
              variant={action.variant}
              className="shrink-0"
              onClick={() => onTransition(job.id, action.status)}
              disabled={isTransitioning}
            >
              {isTransitioning ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <>
                  <TransitionActionIcon status={job.status} className="mr-1 h-3.5 w-3.5" />
                  {action.label}
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Stat cards
// ============================================================

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-1 p-3 text-center sm:p-4">
        <Icon className={`h-5 w-5 ${color}`} />
        <p className="text-base font-bold sm:text-lg">{value}</p>
        <p className="text-[10px] text-muted-foreground sm:text-xs">{label}</p>
      </CardContent>
    </Card>
  );
}

function RatingStatCard({ rating }: { rating: number }) {
  const rounded = Math.round(rating);
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-1 p-3 text-center sm:p-4">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`h-3.5 w-3.5 ${
                i < rounded ? "fill-gold text-gold" : "text-muted-foreground"
              }`}
            />
          ))}
        </div>
        <p className="text-base font-bold sm:text-lg">{rating.toFixed(1)}</p>
        <p className="text-[10px] text-muted-foreground sm:text-xs">On-time rating</p>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Helpers
// ============================================================

function parcelCategoryIcon(category: JobVertical): React.ElementType {
  switch (category) {
    case "water":
      return Droplet;
    case "food":
      return Utensils;
    case "grocery":
      return ShoppingCart;
    case "pharmacy":
      return Pill;
    case "courier":
      return FileText;
    case "errand":
      return ListChecks;
    case "parcel":
    case "other":
    default:
      return Package;
  }
}

function transitionActionIcon(status: DeliveryJob["status"]): React.ElementType | null {
  switch (status) {
    case "assigned":
      return Package;
    case "picked_up":
      return Truck;
    case "in_transit":
      return CheckCircle2;
    default:
      return null;
  }
}

function transitionAction(
  status: DeliveryJob["status"]
): { label: string; status: string; variant: "default" | "outline" | "secondary" } | null {
  switch (status) {
    case "assigned":
      return { label: "Pick Up", status: "picked_up", variant: "default" };
    case "picked_up":
      return { label: "In Transit", status: "in_transit", variant: "secondary" };
    case "in_transit":
      return { label: "Delivered", status: "delivered", variant: "default" };
    default:
      return null;
  }
}

// Dedicated wrapper components for category/action icons. We use
// createElement (rather than assigning the looked-up component to a
// capitalized variable and rendering <Icon />) because the ESLint rule
// react-hooks/static-components treats any capitalized variable assigned
// a component reference during render as "creating a component during
// render" — even though parcelCategoryIcon just returns an existing
// lucide-react icon reference.
function ParcelCategoryIcon({
  category,
  className,
}: {
  category: JobVertical;
  className?: string;
}) {
  return createElement(parcelCategoryIcon(category), { className });
}

function TransitionActionIcon({
  status,
  className,
}: {
  status: DeliveryJob["status"];
  className?: string;
}) {
  const Icon = transitionActionIcon(status);
  return Icon ? createElement(Icon, { className }) : null;
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + "…";
}

// Lightweight mock distance when quotedDistanceKm is null — uses
// coordinate delta as a rough proxy. Falls back to a flat 2.0 km.
function estimateDistanceKm(job: DeliveryJob): number {
  if (
    job.pickupLat != null &&
    job.pickupLng != null &&
    job.dropoffLat != null &&
    job.dropoffLng != null
  ) {
    const dLat = job.dropoffLat - job.pickupLat;
    const dLng = job.dropoffLng - job.pickupLng;
    // Approx km using equirectangular projection (~111km per degree).
    const km = Math.sqrt(dLat * dLat + dLng * dLng) * 111;
    return Math.max(0.5, Math.round(km * 10) / 10);
  }
  return 2.0;
}
